/**
 * The article's own data — reference, designation, brand, family, unit, price —
 * read, and editable by whoever may write articles.
 *
 * These are the fields an ERP synchronisation owns. Editing one here is
 * allowed (the API has always accepted it), but on an article that came from
 * the ERP the next synchronisation would put the ERP's value back. So saving
 * offers to *lock* what was edited — the operator's half of "the ERP wins
 * unless the field is locked" — and it is on by default, because losing an
 * edit silently is the worse surprise.
 *
 * The lock is a separate permission (`integration.mapping.write`): it changes
 * who owns the value, not the value. Without it the edit is still saved and
 * the screen says the lock could not be set.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { day, money } from "@hardauto/core-i18n";
import { Button, Card, Checkbox, DefinitionList, Input, Select, useToast } from "@hardauto/core-ui";

import type { Article, ReferentialApi } from "../api";
import { NAMESPACE } from "../i18n";

/** What `PATCH /articles/{id}` is sent: only what changed. */
type CorePatch = Partial<{
  designation: string;
  brand: string;
  family_code: string;
  sales_unit: string;
  base_price: string;
}>;

/** A price as the API wants it: dot decimal, two places at most. */
function normalisedPrice(text: string): string | null {
  const value = text.trim().replace(",", ".");
  return /^\d+(\.\d{1,2})?$/.test(value) ? value : null;
}

export interface ArticleCoreCardProps {
  api: ReferentialApi;
  client: ApiClient;
  article: Article;
  mayWrite: boolean;
  /** The article changed on the server: read it again. */
  onSaved: () => void;
}

export function ArticleCoreCard(props: ArticleCoreCardProps) {
  const { article, mayWrite } = props;
  const { t, i18n } = useTranslation(NAMESPACE);
  const [editing, setEditing] = useState(false);

  return (
    <Card
      title={t("article.erp.title")}
      compactTitle
      actions={
        mayWrite && !editing ? (
          <Button size="sm" onClick={() => setEditing(true)}>
            {t("article.erp.edit")}
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <CoreForm
          {...props}
          onDone={(saved) => {
            setEditing(false);
            if (saved) props.onSaved();
          }}
        />
      ) : (
        <>
          <DefinitionList
            items={[
              { label: t("article.erp.reference"), value: <code>{article.reference}</code> },
              { label: t("article.erp.designation"), value: article.designation },
              { label: t("article.erp.brand"), value: article.brand || "—" },
              {
                label: t("article.erp.family"),
                value: article.family_code || t("articles.unclassified"),
              },
              { label: t("article.erp.unit"), value: article.sales_unit },
              { label: t("article.erp.price"), value: money(article.base_price, i18n.language) },
              { label: t("article.erp.created"), value: day(article.created_at, i18n.language) },
            ]}
          />
          {/* The design puts a note here saying the ETL overwrites these
              fields. It says so only when that is true: on an installation
              with no connector, claiming it would be a lie about why an
              edit did not stick, and the opposite lie is worse. */}
          <p className="hardauto-article__note">
            {article.external_system ? t("article.erp.note") : t("article.erp.noteLocal")}
          </p>
          {article.locked_fields.length > 0 ? (
            <p className="hardauto-article__note">
              {t("article.erp.locked", { fields: article.locked_fields.join(", ") })}
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

/** The option that opens the "add a brand" input instead of choosing one. */
const NEW_BRAND = "__new__";

/**
 * An article's brand, picked from the brand list.
 *
 * The list is the one spelling of each brand, so choosing beats typing: two
 * people typing "Michelin" and "MICHELIN" make two brands. A missing brand is
 * added on the spot, without leaving the article. Somebody who may not read the
 * catalogue's list gets the plain text field the form always had.
 */
function BrandField({
  api,
  value,
  onChange,
}: {
  api: ReferentialApi;
  value: string;
  onChange: (brand: string) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const fetcher = useCallback(() => api.listBrandChoices().catch(() => null), [api]);
  const list = useResource(fetcher);
  const [added, setAdded] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  // No list to pick from — unreadable, or still loading: the text field.
  if (list.data === null || (list.loading && !list.data)) {
    return (
      <Field label={t("article.erp.brand")}>
        {(props) => (
          <Input
            {...props}
            value={value}
            maxLength={80}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </Field>
    );
  }

  const labels = [...new Set([...list.data, ...added])];
  // The article's own brand stays selectable even when the list does not hold it,
  // so opening the form never silently changes it.
  if (value && !labels.some((label) => label.toLowerCase() === value.toLowerCase())) {
    labels.unshift(value);
  }
  labels.sort((a, b) => a.localeCompare(b));

  return (
    <>
      <Field label={t("article.erp.brand")}>
        {(props) => (
          <Select
            {...props}
            value={labels.includes(value) ? value : ""}
            onChange={(event) => {
              if (event.target.value === NEW_BRAND) setAdding(true);
              else onChange(event.target.value);
            }}
          >
            <option value="">{t("article.erp.noBrand")}</option>
            {labels.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
            <option value={NEW_BRAND}>{t("article.erp.newBrand")}</option>
          </Select>
        )}
      </Field>
      {adding ? (
        <div className="hardauto-inline-form">
          <div className="hardauto-inline-form__field">
            <Field label={t("article.erp.newBrandLabel")}>
              {(props) => (
                <Input
                  {...props}
                  value={draft}
                  maxLength={80}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.preventDefault();
                  }}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__actions">
            <Button
              variant="primary"
              disabled={draft.trim() === ""}
              busy={action.isBusy("brand")}
              onClick={() => {
                const name = draft.trim();
                void action.run("brand", async () => {
                  try {
                    await api.createBrandChoice(name);
                  } catch (error) {
                    // Already in the list, spelled another way: use the one that is there.
                    if (!(error instanceof ApiError && error.status === 409)) {
                      toast.show(error instanceof ApiError ? error.message : t("error.title"), {
                        tone: "danger",
                      });
                      return;
                    }
                  }
                  setAdded((current) => [...current, name]);
                  onChange(name);
                  setAdding(false);
                  setDraft("");
                });
              }}
            >
              {t("article.erp.addBrand")}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              {t("article.erp.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function CoreForm({
  api,
  client,
  article,
  onDone,
}: ArticleCoreCardProps & { onDone: (saved: boolean) => void }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const [designation, setDesignation] = useState(article.designation);
  const [brand, setBrand] = useState(article.brand);
  const [family, setFamily] = useState(article.family_code);
  const [unit, setUnit] = useState(article.sales_unit);
  const [price, setPrice] = useState(String(article.base_price));
  const synced = Boolean(article.external_system);
  const [lock, setLock] = useState(synced);

  const familiesFetcher = useCallback(() => api.listFamilies(false), [api]);
  const families = useResource(familiesFetcher);
  const options = useMemo(() => {
    const items = (families.data?.items ?? []).map((item) => ({
      code: item.code,
      label: item.label ? `${item.label} (${item.code})` : item.code,
    }));
    // The article's own family stays selectable even when it is inactive or
    // missing from the list, so opening the form never silently changes it.
    if (article.family_code && !items.some((item) => item.code === article.family_code)) {
      items.unshift({ code: article.family_code, label: article.family_code });
    }
    return items;
  }, [families.data, article.family_code]);

  const cleanPrice = normalisedPrice(price);
  const patch: CorePatch = {
    ...(designation.trim() !== article.designation ? { designation: designation.trim() } : {}),
    ...(brand.trim() !== article.brand ? { brand: brand.trim() } : {}),
    // The API refuses an empty family: an unclassified article stays so until
    // a family is chosen.
    ...(family && family !== article.family_code ? { family_code: family } : {}),
    ...(unit.trim() !== article.sales_unit ? { sales_unit: unit.trim() } : {}),
    ...(cleanPrice !== null && Number(cleanPrice) !== Number(article.base_price)
      ? { base_price: cleanPrice }
      : {}),
  };
  const changedFields = Object.keys(patch);
  const valid = designation.trim() !== "" && unit.trim() !== "" && cleanPrice !== null;

  return (
    <form
      className="hardauto-stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || changedFields.length === 0) return;
        void action.run("core", async () => {
          try {
            await api.updateArticle(article.id, patch);
          } catch (error) {
            toast.show(error instanceof ApiError ? error.message : t("error.title"), {
              tone: "danger",
            });
            return;
          }
          if (synced && lock) {
            try {
              await client.request(`/api/v1/integration/locks/article/${article.id}`, {
                method: "PUT",
                body: { fields: [...new Set([...article.locked_fields, ...changedFields])] },
              });
              toast.show(t("article.erp.saved"), { tone: "success" });
            } catch {
              toast.show(t("article.erp.lockFailed"), { tone: "warning" });
            }
          } else {
            toast.show(t("article.erp.saved"), { tone: "success" });
          }
          onDone(true);
        });
      }}
    >
      <Field label={t("article.erp.reference")}>
        {(props) => <Input {...props} value={article.reference} readOnly disabled />}
      </Field>
      <Field label={t("article.erp.designation")} required>
        {(props) => (
          <Input
            {...props}
            value={designation}
            maxLength={300}
            onChange={(event) => setDesignation(event.target.value)}
          />
        )}
      </Field>
      <BrandField api={api} value={brand} onChange={setBrand} />
      <Field label={t("article.erp.family")}>
        {(props) => (
          <Select {...props} value={family} onChange={(event) => setFamily(event.target.value)}>
            {article.family_code === "" ? (
              <option value="">{t("articles.unclassified")}</option>
            ) : null}
            {options.map((item) => (
              <option key={item.code} value={item.code}>
                {item.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={t("article.erp.unit")} required>
        {(props) => (
          <Input
            {...props}
            value={unit}
            maxLength={10}
            onChange={(event) => setUnit(event.target.value)}
          />
        )}
      </Field>
      <Field
        label={t("article.erp.price")}
        required
        {...(cleanPrice === null ? { error: t("article.erp.priceInvalid") } : {})}
      >
        {(props) => (
          <Input
            {...props}
            inputMode="decimal"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
        )}
      </Field>

      {synced ? (
        <Checkbox
          label={t("article.erp.lock")}
          checked={lock}
          onChange={(event) => setLock(event.target.checked)}
        />
      ) : null}

      <div className="hardauto-inline-form__actions">
        <Button
          type="submit"
          variant="primary"
          disabled={!valid || changedFields.length === 0}
          busy={action.isBusy("core")}
          busyLabel={t("article.erp.save")}
        >
          {t("article.erp.save")}
        </Button>
        <Button variant="ghost" onClick={() => onDone(false)}>
          {t("article.erp.cancel")}
        </Button>
      </div>
    </form>
  );
}
